import { useMutation, useQueryClient } from '@tanstack/react-query';

import { QueryKey } from ':cloud/queries/types';
import { APIClientBase } from ':cloud/state/Api';
import { ConfigurePlatformTokenResponse } from ':cloud/types/service_proto';

const errorMsg = 'An error occurred configuring the Client API Key. Please retry';

type UpdateTokenProps = {
  organizationId: string;
  projectId: string;
  tokenId: string;
  allowedDomains: string[];
  updateMask: string;
};

async function updateToken({
  organizationId,
  projectId,
  tokenId,
  allowedDomains,
  updateMask,
}: UpdateTokenProps): Promise<ConfigurePlatformTokenResponse | undefined> {
  const path = `/apikeys/v1/organizations/${organizationId}/projects/${projectId}/tokens/${tokenId}`;
  const body = {
    token: {
      id: tokenId,
      allowedDomains,
    },
    updateMask,
  };
  const { data } = await APIClientBase.put<ConfigurePlatformTokenResponse>(path, body);
  return data;
}

type UpdateTokenMutate = {
  tokenId: string;
  allowedDomains: string[];
  updateMask: string;
};

export function useUpdateToken(organizationId, projectId, onSuccess?: (clientId: string) => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tokenId, allowedDomains, updateMask }: UpdateTokenMutate) =>
      updateToken({ organizationId, projectId, tokenId, allowedDomains, updateMask }),
    onSuccess: async (deletedToken) => {
      if (!deletedToken) return;
      await queryClient.invalidateQueries([QueryKey.tokens, organizationId]);
      onSuccess?.(deletedToken.id);
    },
    meta: {
      errorMsg,
    },
  });
}
